import { useCallback, useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { Wallet, Sparkles } from "lucide-react";

import TopNav from "@/components/smartpublish/TopNav";
import GradientOrb from "@/components/smartpublish/GradientOrb";
import Uploader from "@/components/smartpublish/Uploader";
import AgentLoading from "@/components/smartpublish/AgentLoading";
import SuggestionSection from "@/components/smartpublish/SuggestionSection";
import SchedulePanel from "@/components/smartpublish/SchedulePanel";
import Agent2Card from "@/components/smartpublish/Agent2Card";
import AdsumErrorBoundary from "@/components/smartpublish/AdsumErrorBoundary";
import AdsumDrawer from "@/components/smartpublish/AdsumDrawer";
import AdsumInlineTrigger from "@/components/smartpublish/AdsumInlineTrigger";
import useAdsumState from "@/hooks/useAdsumState";
import ConfirmationPanel from "@/components/smartpublish/ConfirmationPanel";

import { useSession } from "@/contexts/SessionContext";
import { usePulse } from "@/contexts/PulseContext";
import { api } from "@/lib/api";

const PLATFORM_LABEL = {
    tiktok: "TikTok",
    instagram: "Instagram",
    facebook: "Facebook",
};

const DAY_INDEX = {
    sunday: 0,
    monday: 1,
    tuesday: 2,
    wednesday: 3,
    thursday: 4,
    friday: 5,
    saturday: 6,
};

function parseSlotFromRec(rec) {
    if (!rec) return null;
    // day_of_week -> next occurrence
    const dayKey = String(rec.day_of_week || "").trim().toLowerCase();
    const todayIdx = new Date().getDay();
    const targetIdx = DAY_INDEX[dayKey];
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    if (targetIdx !== undefined) {
        let diff = (targetIdx - todayIdx + 7) % 7;
        if (diff === 0) diff = 7; // next week, not today
        date.setDate(date.getDate() + diff);
    } else {
        date.setDate(date.getDate() + 1);
    }
    // time_local "4:00 PM" -> hour12=4, minute="00", meridiem="PM"
    const match = String(rec.time_local || "").match(
        /^(\d{1,2})\s*[:.\u2236]\s*(\d{1,2})\s*(AM|PM|am|pm)?/
    );
    let hour12 = 9;
    let minute = "00";
    let meridiem = "AM";
    if (match) {
        hour12 = parseInt(match[1], 10);
        const m = parseInt(match[2], 10);
        // snap to nearest valid minute slot
        const allowed = [0, 15, 30, 45];
        const snapped = allowed.reduce((a, b) =>
            Math.abs(b - m) < Math.abs(a - m) ? b : a
        );
        minute = String(snapped).padStart(2, "0");
        meridiem = (match[3] || "AM").toUpperCase();
        if (hour12 === 0) hour12 = 12;
        if (hour12 > 12) {
            hour12 = hour12 % 12 || 12;
            meridiem = meridiem === "AM" ? "PM" : meridiem;
        }
    }
    return {
        date,
        hour12,
        minute,
        meridiem,
        platforms: [rec.platform],
    };
}

export default function SmartPublishWorkspace() {
    const { setBalanceFast, openDrawer, tier, openSubscriptionDrawer, profile } = useSession();
    const { setContext: setPulseContext } = usePulse();
    const adsum = useAdsumState();

    const [uploadState, setUploadState] = useState({
        file: null,
        previewUrl: null,
        kind: null,
        textContent: null,
        progress: 0,
    });
    const [analyzing, setAnalyzing] = useState(false);
    const [agent1, setAgent1] = useState(null);
    const [insufficient, setInsufficient] = useState(null); // {required, balance}
    const [selectedTitleIdx, setSelectedTitleIdx] = useState(null);
    const [selectedCaptionIdx, setSelectedCaptionIdx] = useState(null);
    const [editedTitles, setEditedTitles] = useState({});
    const [editedCaptions, setEditedCaptions] = useState({});
    const [scheduling, setScheduling] = useState(false);
    const [confirmation, setConfirmation] = useState(null);
    const [slotSeed, setSlotSeed] = useState(null);
    const [scheduleState, setScheduleState] = useState(null);

    const resetAll = useCallback(() => {
        if (uploadState.previewUrl) {
            URL.revokeObjectURL(uploadState.previewUrl);
        }
        setUploadState({
            file: null,
            previewUrl: null,
            kind: null,
            textContent: null,
            progress: 0,
        });
        setAnalyzing(false);
        setAgent1(null);
        setInsufficient(null);
        setSelectedTitleIdx(null);
        setSelectedCaptionIdx(null);
        setEditedTitles({});
        setEditedCaptions({});
        setConfirmation(null);
        setSlotSeed(null);
        setScheduleState(null);
        adsum.resetForNewAsset();
        adsum.setOpen(false);
    }, [uploadState.previewUrl, adsum]);

    const analyzeAsset = useCallback(
        async ({ file, text }) => {
            setAnalyzing(true);
            setAgent1(null);
            setInsufficient(null);
            setSelectedTitleIdx(null);
            setSelectedCaptionIdx(null);
            setEditedTitles({});
            setEditedCaptions({});
            setConfirmation(null);
            setSlotSeed(null);
            setScheduleState(null);
            adsum.resetForNewAsset();

            const form = new FormData();
            if (file) form.append("file", file);
            else if (text) form.append("text", text);

            try {
                const { data } = await api.post("/agent1/analyze", form, {
                    onUploadProgress: (e) => {
                        if (e.total) {
                            setUploadState((s) => ({
                                ...s,
                                progress: Math.round((e.loaded * 100) / e.total),
                            }));
                        }
                    },
                });
                setAgent1(data);
                if (typeof data.balance_after === "number") {
                    setBalanceFast(data.balance_after);
                }
                toast.success("Agent 1 done", {
                    description: `${data.titles.length} titles and ${data.captions.length} captions ready.`,
                });
            } catch (err) {
                const status = err?.response?.status;
                const detail = err?.response?.data?.detail;
                if (status === 402) {
                    const info =
                        typeof detail === "object"
                            ? detail
                            : { required: 5, balance: 0 };
                    setInsufficient({
                        required: info.required ?? 5,
                        balance: info.balance ?? 0,
                    });
                    toast.error("Vault is low", {
                        description: "Top up to keep scanning.",
                    });
                } else {
                    toast.error("Analysis failed", {
                        description:
                            typeof detail === "string"
                                ? detail
                                : detail?.error || err?.message || "Try again.",
                    });
                }
            } finally {
                setAnalyzing(false);
            }
        },
        [setBalanceFast, adsum]
    );

    const handleFileAccepted = useCallback(
        (file, kind, textContent) => {
            const previewUrl =
                kind === "video" || kind === "image"
                    ? URL.createObjectURL(file)
                    : null;
            setUploadState({
                file,
                previewUrl,
                kind,
                textContent: textContent || null,
                progress: 0,
            });
            analyzeAsset({ file });
        },
        [analyzeAsset]
    );

    const handleTextPaste = useCallback(
        (textValue) => {
            const blob = new Blob([textValue], { type: "text/plain" });
            const pseudoFile = new File([blob], "pasted.txt", {
                type: "text/plain",
            });
            setUploadState({
                file: pseudoFile,
                previewUrl: null,
                kind: "text",
                textContent: textValue,
                progress: 0,
            });
            analyzeAsset({ text: textValue });
        },
        [analyzeAsset]
    );

    const getTitleText = (idx) =>
        editedTitles[idx] !== undefined
            ? editedTitles[idx]
            : agent1?.titles?.[idx]?.text || "";

    const getCaptionText = (idx) =>
        editedCaptions[idx] !== undefined
            ? editedCaptions[idx]
            : agent1?.captions?.[idx]?.text || "";

    const canSchedule =
        agent1 != null &&
        selectedTitleIdx !== null &&
        selectedCaptionIdx !== null;

    const handleUseSlot = useCallback((rec) => {
        const slot = parseSlotFromRec(rec);
        if (!slot) return;
        // tag with timestamp so identical slots still re-trigger the effect
        setSlotSeed({ ...slot, _ts: Date.now() });
        toast.success("Slot loaded", {
            description: `${PLATFORM_LABEL[rec.platform] || rec.platform} · ${rec.day_of_week} ${rec.time_local}`,
        });
    }, []);

    const handleApplyAdsumTweak = useCallback(
        ({ rewritten_title, rewritten_caption, rewritten_hashtags }) => {
            if (selectedTitleIdx !== null && rewritten_title) {
                setEditedTitles((s) => ({
                    ...s,
                    [selectedTitleIdx]: rewritten_title,
                }));
            }
            if (selectedCaptionIdx !== null && rewritten_caption) {
                setEditedCaptions((s) => ({
                    ...s,
                    [selectedCaptionIdx]: rewritten_caption,
                }));
            }
            if (
                selectedCaptionIdx !== null &&
                Array.isArray(rewritten_hashtags)
            ) {
                setAgent1((prev) => {
                    if (!prev) return prev;
                    const newCaptions = [...(prev.captions || [])];
                    if (newCaptions[selectedCaptionIdx]) {
                        newCaptions[selectedCaptionIdx] = {
                            ...newCaptions[selectedCaptionIdx],
                            hashtags: rewritten_hashtags,
                        };
                    }
                    return { ...prev, captions: newCaptions };
                });
            }
            adsum.markApplied();
            toast.success("Adsum tweak applied", {
                description: "Title, caption and hashtags updated.",
            });
            // Linger on the result for ~1.2s, then close so user can verify
            // the rewrite landed on the main compose cards.
            setTimeout(() => adsum.setOpen(false), 1200);
        },
        [selectedTitleIdx, selectedCaptionIdx, adsum]
    );

    const handleScheduleChange = useCallback((next) => {
        setScheduleState((prev) => {
            if (
                prev &&
                prev.scheduledFor === next.scheduledFor &&
                prev.platforms.length === next.platforms.length &&
                prev.platforms.every((p, i) => p === next.platforms[i])
            ) {
                return prev;
            }
            return next;
        });
    }, []);

    // Phase 5-B: push live compose state into Pulse so the chat drawer has
    // fresh context — current asset_id, niche, selected title/caption, and
    // the live platform list.
    const titleTextForPulse =
        selectedTitleIdx !== null && agent1
            ? getTitleText(selectedTitleIdx)
            : null;
    const captionTextForPulse =
        selectedCaptionIdx !== null && agent1
            ? getCaptionText(selectedCaptionIdx)
            : null;
    const pulsePlatforms = useMemo(
        () => scheduleState?.platforms || [],
        [scheduleState]
    );
    useEffect(() => {
        setPulseContext({
            asset_id: agent1?.asset_id || null,
            niche: profile?.niche || null,
            platforms: pulsePlatforms,
            last_title: titleTextForPulse,
            last_caption: captionTextForPulse,
        });
    }, [
        agent1?.asset_id,
        profile?.niche,
        pulsePlatforms,
        titleTextForPulse,
        captionTextForPulse,
        setPulseContext,
    ]);

    const handleAdsumRun = useCallback(
        (payload) => {
            adsum.runReview(payload, {
                setBalanceFast,
                openVault: openDrawer,
            });
        },
        [adsum, setBalanceFast, openDrawer]
    );

    const handleSchedule = async ({ scheduledFor, platforms }) => {
        if (!agent1 || selectedTitleIdx === null || selectedCaptionIdx === null) {
            toast.error("Pick a title and a caption first");
            return;
        }
        setScheduling(true);
        try {
            const payload = {
                asset_id: agent1.asset_id,
                title: getTitleText(selectedTitleIdx),
                caption: getCaptionText(selectedCaptionIdx),
                hashtags: agent1.captions[selectedCaptionIdx]?.hashtags || [],
                platforms,
                scheduled_for: scheduledFor,
            };
            const { data } = await api.post("/posts/schedule", payload);
            setConfirmation(data);
            toast.success("Marked as Ready", {
                description: `Planned for ${new Date(data.scheduled_for).toLocaleString()}`,
            });
        } catch (err) {
            const detail = err?.response?.data?.detail || err?.message;
            toast.error("Couldn't mark as ready", { description: String(detail) });
        } finally {
            setScheduling(false);
        }
    };

    return (
        <div className="relative min-h-screen overflow-x-hidden bg-[#0B0F19] text-[#E8ECF5]">
            <GradientOrb />
            <TopNav onNewPost={resetAll} />

            <main className="relative z-10 mx-auto flex w-full max-w-[960px] flex-col gap-12 px-6 py-12 sm:py-16">
                {!confirmation && (
                    <motion.section
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
                        className="flex flex-col gap-3"
                    >
                        <span
                            className="text-[10px] uppercase tracking-[0.32em] text-[#8A93A6]"
                            data-testid="hero-eyebrow"
                        >
                            phase 02 · agents 1 + 2
                        </span>
                        <h1
                            className="text-4xl font-medium tracking-[-0.02em] text-[#E8ECF5] sm:text-5xl"
                            data-testid="hero-title"
                        >
                            Drop content.
                            <br />
                            Let the agents
                            <span className="sp-glow-mint"> orchestrate</span>.
                        </h1>
                        <p
                            className="max-w-xl text-base text-[#8A93A6]"
                            data-testid="hero-subtitle"
                        >
                            Upload a video, image, or paste raw text. SmartPublish
                            analyses it natively, writes platform-tuned titles,
                            captions and hashtags, and predicts the optimal post
                            slot per platform.
                        </p>
                    </motion.section>
                )}

                {!agent1 && !analyzing && !confirmation && !insufficient && (
                    <Uploader
                        onFileAccepted={handleFileAccepted}
                        onTextPaste={handleTextPaste}
                        uploadState={uploadState}
                        onReset={resetAll}
                    />
                )}

                {insufficient && !confirmation && (
                    <InsufficientCard
                        info={insufficient}
                        onTopUp={() => openDrawer("topup")}
                        onReset={() => setInsufficient(null)}
                    />
                )}

                {(analyzing || (uploadState.file && agent1)) && !confirmation && (
                    <motion.section
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4 }}
                        className="flex flex-col gap-3"
                        data-testid="asset-preview-section"
                    >
                        <span className="text-[10px] uppercase tracking-[0.32em] text-[#8A93A6]">
                            asset
                        </span>
                        <AssetPreview
                            uploadState={uploadState}
                            onReset={resetAll}
                        />
                    </motion.section>
                )}

                <AnimatePresence mode="wait">
                    {analyzing && (
                        <motion.div
                            key="loading"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            data-testid="agent1-loading"
                        >
                            <AgentLoading />
                        </motion.div>
                    )}
                </AnimatePresence>

                {agent1 && !analyzing && !confirmation && (
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        className="flex flex-col gap-10"
                    >
                        <p
                            className="font-mono text-sm italic text-[#8A93A6]"
                            data-testid="asset-summary"
                        >
                            Agent 1 detected: {agent1.asset_summary}
                        </p>

                        <SuggestionSection
                            kind="title"
                            label="Suggested Titles"
                            items={agent1.titles}
                            selectedIdx={selectedTitleIdx}
                            edited={editedTitles}
                            onSelect={setSelectedTitleIdx}
                            onEdit={(idx, value) =>
                                setEditedTitles((s) => ({ ...s, [idx]: value }))
                            }
                            platformLabel={PLATFORM_LABEL}
                        />

                        <SuggestionSection
                            kind="caption"
                            label="Suggested Captions"
                            items={agent1.captions}
                            selectedIdx={selectedCaptionIdx}
                            edited={editedCaptions}
                            onSelect={setSelectedCaptionIdx}
                            onEdit={(idx, value) =>
                                setEditedCaptions((s) => ({ ...s, [idx]: value }))
                            }
                            platformLabel={PLATFORM_LABEL}
                        />

                        <AnimatePresence>
                            {canSchedule && (
                                <motion.div
                                    key="schedule-block"
                                    initial={{ opacity: 0, y: 12 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: 8 }}
                                    transition={{ duration: 0.45 }}
                                    className="flex flex-col gap-6"
                                >
                                    <SchedulePanel
                                        onSubmit={handleSchedule}
                                        scheduling={scheduling}
                                        defaultPlatforms={[
                                            agent1.titles[selectedTitleIdx]?.platform,
                                            agent1.captions[selectedCaptionIdx]?.platform,
                                        ].filter(Boolean)}
                                        seedSlot={slotSeed}
                                        onScheduleChange={handleScheduleChange}
                                        // eslint-disable-next-line react/no-unstable-nested-components
                                        slot={({ platforms: livePlatforms }) => (
                                            <Agent2Card
                                                assetId={agent1.asset_id}
                                                platforms={livePlatforms}
                                                onUseSlot={handleUseSlot}
                                            />
                                        )}
                                        preSubmitRow={
                                            <AdsumInlineTrigger
                                                onOpen={adsum.toggle}
                                                phase={adsum.phase}
                                                review={adsum.review}
                                            />
                                        }
                                    />
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </motion.div>
                )}

                {confirmation && (
                    <ConfirmationPanel
                        confirmation={confirmation}
                        onAnother={resetAll}
                    />
                )}
            </main>

            <footer className="relative z-10 mx-auto w-full max-w-[960px] px-6 pb-10 pt-4">
                <p
                    className="font-mono text-xs tracking-wide text-[#8A93A6]/80"
                    data-testid="footer-text"
                >
                    SmartPublish · orchestrating content
                </p>
            </footer>

            {/* Phase 5-B: the floating orb slot is now owned by Pulse (mounted
                globally in App.js). The Adsum trigger lives inline inside
                SchedulePanel. The AdsumDrawer itself still mounts here — same
                hook state, same Apply-tweak wiring. */}
            <AdsumErrorBoundary>
                <AdsumDrawer
                    open={adsum.open}
                    onOpenChange={adsum.setOpen}
                    phase={adsum.phase}
                    review={adsum.review}
                    error={adsum.error}
                    applied={adsum.applied}
                    assetId={agent1?.asset_id}
                    title={
                        selectedTitleIdx !== null
                            ? getTitleText(selectedTitleIdx)
                            : ""
                    }
                    caption={
                        selectedCaptionIdx !== null
                            ? getCaptionText(selectedCaptionIdx)
                            : ""
                    }
                    hashtags={
                        selectedCaptionIdx !== null
                            ? agent1?.captions?.[selectedCaptionIdx]?.hashtags || []
                            : []
                    }
                    scheduledFor={scheduleState?.scheduledFor || null}
                    platforms={scheduleState?.platforms || []}
                    onRun={handleAdsumRun}
                    onApplyTweak={handleApplyAdsumTweak}
                />
            </AdsumErrorBoundary>
        </div>
    );
}

function InsufficientCard({ info, onTopUp, onReset }) {
    return (
        <motion.section
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col items-start gap-4 rounded-2xl border border-[#FF5470]/30 bg-[#111726] p-7"
            data-testid="insufficient-card"
        >
            <div className="flex items-center gap-2">
                <Wallet className="h-4 w-4 text-[#FF5470]" />
                <span className="text-[10px] uppercase tracking-[0.32em] text-[#FF5470]">
                    nexavault is low
                </span>
            </div>
            <h2 className="text-xl font-medium tracking-tight text-[#E8ECF5]">
                Top up to keep scanning.
            </h2>
            <p className="max-w-md text-sm text-[#8A93A6]">
                Agent 1 costs <span className="text-[#00F5D4]">{info.required} NXT</span>{" "}
                per run. You currently have{" "}
                <span className="font-mono">{info.balance}</span> NXT — add 500 for $5
                to keep orchestrating.
            </p>
            <div className="flex flex-wrap items-center gap-2">
                <button
                    type="button"
                    onClick={onTopUp}
                    className="group relative overflow-hidden rounded-lg bg-gradient-to-r from-[#2F58CD] to-[#2F58CD] px-4 py-2 text-sm font-medium text-white transition-all hover:shadow-[0_0_22px_rgba(0,245,212,0.4)]"
                    data-testid="insufficient-topup-btn"
                >
                    <span className="relative z-10 inline-flex items-center gap-2">
                        <Sparkles className="h-3.5 w-3.5" />
                        Top up $5 → +500 NXT
                    </span>
                    <span
                        aria-hidden
                        className="absolute inset-0 bg-gradient-to-r from-transparent via-[#00F5D4]/35 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                    />
                </button>
                <button
                    type="button"
                    onClick={onReset}
                    className="rounded-md border border-[#1F2A40] px-3 py-2 text-xs text-[#8A93A6] hover:border-[#1F2A40] hover:text-[#E8ECF5]"
                    data-testid="insufficient-reset-btn"
                >
                    Cancel
                </button>
            </div>
        </motion.section>
    );
}

function AssetPreview({ uploadState, onReset }) {
    if (!uploadState.file) return null;
    return (
        <div
            className="flex flex-col gap-4 rounded-2xl border border-[#1F2A40] bg-[#111726] p-5"
            data-testid="asset-preview-card"
        >
            <div className="flex items-center justify-between gap-4">
                <div className="flex flex-col">
                    <span
                        className="font-mono text-xs text-[#8A93A6]"
                        data-testid="asset-filename"
                    >
                        {uploadState.file.name}
                    </span>
                    <span className="text-[10px] uppercase tracking-[0.3em] text-[#8A93A6]/70">
                        {uploadState.kind}
                    </span>
                </div>
                <button
                    type="button"
                    onClick={onReset}
                    className="rounded-md border border-[#1F2A40] bg-[#0B0F19] px-3 py-1.5 text-xs text-[#8A93A6] transition-colors hover:border-[#FF5470]/60 hover:text-[#FF5470]"
                    data-testid="asset-reset-btn"
                >
                    Replace
                </button>
            </div>

            {uploadState.kind === "video" && uploadState.previewUrl && (
                <video
                    src={uploadState.previewUrl}
                    controls
                    className="w-full rounded-xl border border-[#1F2A40] bg-black"
                    data-testid="asset-video-preview"
                />
            )}
            {uploadState.kind === "image" && uploadState.previewUrl && (
                <img
                    src={uploadState.previewUrl}
                    alt="upload preview"
                    className="max-h-[360px] w-full rounded-xl border border-[#1F2A40] object-contain"
                    data-testid="asset-image-preview"
                />
            )}
            {uploadState.kind === "text" && uploadState.textContent && (
                <pre
                    className="max-h-[260px] overflow-auto rounded-xl border border-[#1F2A40] bg-[#0B0F19] p-4 font-mono text-xs leading-relaxed text-[#E8ECF5]"
                    data-testid="asset-text-preview"
                >
                    {uploadState.textContent
                        .split("\n")
                        .slice(0, 20)
                        .join("\n")}
                </pre>
            )}
        </div>
    );
}
